import { createQuery, QueryableRow } from "@nova/queryable"
import { DistanceUnit, formatAltitude, formatDistance, formatSpeed } from "@nova/format"
import { useBatchPilotTrackingQuery } from "features/pilot-tracking/PilotTrackingQueries"
import { useMemo, useState } from "react"
import { CardCollection } from "features/pilot-tracking/CardCollection"
import classNames from "classnames"
import * as Icons from "@nova/component-icons"
import { ExpandableContent } from "@nova/component-expandable-content"
import { ScrollableArea } from "@nova/component-scrollable-area"
import { ProfilePhoto } from "features/data-management/workspace/workspace/body/settings-and-collaborators/profile/ProfilePhoto"
import { useRouter } from "pages"
import { useAppSelector } from "integrations/redux/hooks"
import { selectWorkspaceDistanceUnit } from "features/data-management/workspace/WorkspaceManagementSlice"
import { selectAllRegisteredMembers } from "features/entity-caching/members/UserManagementSlice"

const bestFlightPerPilotQuery = (
    field: "max_speed" | "distance_flown" | "airtime" | "max_altitude"
) =>
    createQuery({
        resource: "metadata",
        select: ["mission.user_id", "mission_id"],
        distinctOn: "mission.user_id",
        filters: [{ field, op: "IS NOT NULL", value: null }],
        aggregates: [{ field, func: "MAX", alias: "value" }],
        orderBy: [
            { field: "mission.user_id", dir: "ASC" },
            { field: "value", dir: "DESC" },
        ],
    })

const pilotTrackingRecordData = {
    fastestSpeed: {
        title: "Fastest Speed",
        imageSrc: "/images/pilot-tracking-records/fastest-speed.webp",
    },
    longestDistance: {
        title: "Longest Distance",
        imageSrc: "/images/pilot-tracking-records/longest-distance.webp",
    },
    longestFlightTime: {
        title: "Longest Flight Time",
        imageSrc: "/images/pilot-tracking-records/longest-flight-time.webp",
    },
    highestAltitude: {
        title: "Highest Altitude",
        imageSrc: "/images/pilot-tracking-records/highest-altitude.webp",
    },
    milesCovered: {
        title: "Miles Covered",
        imageSrc: "/images/pilot-tracking-records/miles-covered.webp",
    },
}

const recordQueries = {
    fastestSpeed: bestFlightPerPilotQuery("max_speed"),
    longestDistance: bestFlightPerPilotQuery("distance_flown"),
    longestFlightTime: bestFlightPerPilotQuery("airtime"),
    highestAltitude: bestFlightPerPilotQuery("max_altitude"),
    milesCovered: createQuery({
        resource: "metadata",
        select: ["mission.user_id"],
        aggregates: [{ field: "distance_flown", func: "SUM", alias: "value" }],
        orderBy: [{ field: "value", dir: "DESC" }],
        limit: 10,
    }),
}

function getRecordDisplayValue(
    record: PilotTrackingRecord,
    value: number,
    distanceUnit: DistanceUnit
): string {
    switch (record) {
        case "fastestSpeed":
            return formatSpeed({ value, unit: distanceUnit })
        case "longestDistance":
        case "milesCovered":
            return formatDistance({ value, unit: distanceUnit })
        case "longestFlightTime":
            return `${Math.round(value / 60)} minutes`
        case "highestAltitude":
            return formatAltitude({ value, unit: distanceUnit })
    }
}
type PilotTrackingRecord = keyof typeof pilotTrackingRecordData
const pilotTrackingRecords = Object.keys(pilotTrackingRecordData) as PilotTrackingRecord[]

export function RecordsSection() {
    const { data } = useBatchPilotTrackingQuery(recordQueries)
    return (
        <div className="flex flex-col gap-3">
            <div className="text-gray-50 text-[22px] telegraf">Records</div>
            <CardCollection
                cards={pilotTrackingRecords.map((r) => (
                    <Record key={r} record={r} rows={data?.[r]} />
                ))}
                orientation="vertical"
            />
        </div>
    )
}

function Record({
    record,
    rows,
}: {
    record: PilotTrackingRecord
    rows: QueryableRow<(typeof recordQueries)[PilotTrackingRecord]>[] | undefined
}) {
    const router = useRouter("/workspace/:workspaceId")
    const [isOpen, setIsOpen] = useState(false)
    const recordData = pilotTrackingRecordData[record]
    const distanceUnit = useAppSelector(selectWorkspaceDistanceUnit)
    const users = useAppSelector(selectAllRegisteredMembers)
    const richData = useMemo(() => {
        return (
            rows
                ?.toSorted((a, b) => (b.value ?? 0) - (a.value ?? 0))
                .slice(0, 10)
                .map((d) => {
                    const user = users?.find((u) => u.id === d["mission.user_id"])
                    const displayValue =
                        d.value !== null
                            ? getRecordDisplayValue(record, d.value, distanceUnit)
                            : "--"
                    return {
                        userId: d["mission.user_id"],
                        missionId: "mission_id" in d ? d.mission_id : undefined,
                        userFullName: user?.fullName ?? "Unknown User",
                        value: d.value,
                        displayValue,
                    }
                }) ?? []
        )
    }, [rows, distanceUnit, record, users])
    const winner = richData.at(0)
    return (
        <div className="w-full">
            <div
                className="flex gap-3 w-full px-4 py-3 group hover:bg-white-100 transition-colors select-none cursor-pointer"
                onClick={() => setIsOpen((p) => !p)}
            >
                <div className="w-[76px] min-w-[76px] max-w-[76px] h-[76px] min-h-[76px] max-h-[76px] overflow-hidden rounded-lg flex justify-center items-center relative p-0.5">
                    <PilotTrackingRecordImage record={record} isOpen={isOpen} />
                </div>
                <div className="flex-1 flex items-center h-fit gap-[72px]">
                    <div
                        className={classNames(
                            "text-2xl telegraf transition-colors w-[349px] shrink-0",
                            {
                                "text-gray-100 group-hover:text-white": !isOpen,
                                "text-white": isOpen,
                            }
                        )}
                    >
                        {recordData.title}
                    </div>
                    <router.Link
                        to={{
                            path: "/fleet/pilots/:pilotId",
                            params: { pilotId: winner?.userId ?? "" },
                        }}
                    >
                        <div
                            className={classNames("font-mono hover:text-blue-500 hover:underline", {
                                "text-gray-300": !isOpen,
                                "text-gray-50": isOpen,
                            })}
                        >
                            {winner?.userFullName ?? "--"}
                        </div>
                    </router.Link>
                    <div
                        className={classNames("font-mono grow shrink-0 text-right", {
                            "text-gray-400": !isOpen,
                            "text-gray-100": isOpen,
                        })}
                    >
                        {winner?.displayValue ?? "--"}
                    </div>
                </div>
            </div>
            <ExpandableContent isOpen={isOpen}>
                <div className="pl-[172px]">
                    <ScrollableArea horizontal>
                        <div className="flex items-start pt-4 pb-8">
                            {richData.map((d, i) => (
                                <RecordUser key={d.userId ?? i} data={d} placement={i + 1} />
                            ))}
                        </div>
                    </ScrollableArea>
                </div>
            </ExpandableContent>
        </div>
    )
}

const getReadablePlacement = (placement: number) => {
    if (placement === 1) return "First Place"
    if (placement === 2) return "Second Place"
    if (placement === 3) return "Third Place"
}
function RecordUser({
    data,
    placement,
}: {
    data: {
        userId: string | null
        missionId?: string
        userFullName: string
        value: number | null
        displayValue: string
    }
    placement: number
}) {
    const router = useRouter("/workspace/:workspaceId")
    const card = (
        <div className="w-[168px] shrink-0 flex flex-col gap-5 p-4">
            <div className="h-[132px] overflow-hidden rounded-2xl relative">
                <ProfilePhoto
                    userId={data.userId ?? ""}
                    fullName={data.userFullName}
                    initialsPlacement="bottom-left"
                    initialsFontSize={20}
                    initialsFont="mono"
                />
                <PlacementOverlay place={placement} className="rounded-2xl" />
            </div>
            <div className="flex flex-col gap-3">
                <div className="h-[22px] flex justify-between items-center">
                    {placement <= 3 && (
                        <img
                            src={`/images/pilot-tracking-records/placement-${placement}.webp`}
                            alt={getReadablePlacement(placement)}
                            className="w-[38px]"
                        />
                    )}
                    {placement > 3 && <div className="font-mono text-[16px]">{placement}</div>}
                    {data.missionId && (
                        <Icons.Icon
                            Icon={Icons.ArrowRight}
                            className="text-blue-500 invisible group-hover:visible"
                        />
                    )}
                </div>
                <div className="font-mono text-white">{data.userFullName}</div>
                <small className="xs text-gray-100">{data.displayValue}</small>
            </div>
        </div>
    )
    return data.missionId ? (
        <router.Link
            to={{ path: "/fleet/flights/:missionId", params: { missionId: data.missionId } }}
            className="group rounded-[20px] hover:bg-white-100 transition-colors"
        >
            {card}
        </router.Link>
    ) : (
        card
    )
}

const placementOverlayStyles = [
    {
        background:
            "linear-gradient(180deg, rgba(128, 118, 0, 0.00) 0%, rgba(255, 234, 0, 0.40) 100%)",
        boxShadow: "inset 0 0 0 3px rgba(255, 255, 255, 0.2)",
    },
    {
        background:
            "linear-gradient(180deg, rgba(50, 50, 50, 0.40) 0%, rgba(184, 184, 184, 0.40) 100%)",
        boxShadow: "inset 0 0 0 3px rgba(255, 255, 255, 0.2)",
    },
    {
        background:
            "linear-gradient(180deg, rgba(99, 46, 0, 0.40) 0%, rgba(255, 119, 0, 0.40) 100%)",
        boxShadow: "inset 0 0 0 3px rgba(255, 255, 255, 0.2)",
    },
]

function PlacementOverlay({ place, className }: { place: number; className?: string }) {
    if (place > 3 || place < 1) return null
    return (
        <div
            className={classNames("absolute inset-0", className)}
            style={placementOverlayStyles[place - 1]}
        />
    )
}

function PilotTrackingRecordImage({
    record,
    isOpen,
}: {
    record: PilotTrackingRecord
    isOpen: boolean
}) {
    const recordData = pilotTrackingRecordData[record]
    const [hasLoadedBorder, setHasLoadedBorder] = useState(false)
    const [hasLoadedImage, setHasLoadedImage] = useState(false)
    return (
        <div
            className={classNames("w-full h-full", {
                "opacity-0": !hasLoadedBorder || !hasLoadedImage,
            })}
        >
            <img
                src="/images/pilot-tracking-records/open-record-border.webp"
                alt=""
                className={classNames("absolute inset-0 w-full h-full transition-transform", {
                    "scale-90": !isOpen,
                    "scale-100": isOpen,
                })}
                onLoad={() => setHasLoadedBorder(true)}
                onError={() => setHasLoadedBorder(true)}
            />
            <div
                className={classNames(
                    "w-full h-full overflow-hidden rounded-lg border transition-all",
                    {
                        "border-white-100 group-hover:border-white-500 grayscale group-hover:grayscale-0 brightness-50 group-hover:brightness-100":
                            !isOpen,
                        "border-transparent": isOpen,
                    }
                )}
            >
                <div className="w-full h-full relative flex justify-center items-center">
                    <img
                        key={record}
                        src={recordData.imageSrc}
                        alt={recordData.title}
                        className="absolute inset-0 w-full h-full object-cover"
                        onLoad={() => setHasLoadedImage(true)}
                    />
                </div>
            </div>
        </div>
    )
}
